import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';
import {
  STATUS_ICON_CHECK_O, STATUS_ICON_DISABLED,
  STATUS_ICON_LOADING, STATUS_ICON_MAINTENANCE,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_WARNING, STATUS_ICON_WARNING_O
} from '@/common/utils/statuses';

export enum COMPUTE_SERVICE_TYPE {
  controller = 'controller',
  worker = 'worker'
}

export enum COMPUTE_ROLE {
  controller = 'controller',
  compute = 'compute'
}

export const COMPUTE_ROLES = [
  COMPUTE_ROLE.controller,
  COMPUTE_ROLE.compute
];

type ComputeRoleText = Record<COMPUTE_ROLE, string>

export const COMPUTE_ROLE_TEXT = computedLocale<ComputeRoleText>(() => ({
  [COMPUTE_ROLE.controller]: i18n.t('Tag~Controller'),
  [COMPUTE_ROLE.compute]: i18n.t('Tag~Worker')
}));

type ComputeRoleTagText = Record<COMPUTE_ROLE, string>

export const COMPUTE_ROLE_TAG_TEXT = computedLocale<ComputeRoleTagText>(() => ({
  [COMPUTE_ROLE.controller]: i18n.t('Tag~ComputeController'),
  [COMPUTE_ROLE.compute]: i18n.t('Tag~ComputeWorker')
}));

export enum COMPUTE_SERVICE_CATEGORY {
  storage = 'storage',
  network = 'network',
  compute = 'compute'
}

type ComputeServiceCategoryLabel = Record<COMPUTE_SERVICE_CATEGORY, string>

export const COMPUTE_SERVICE_CATEGORY_LABEL = computedLocale<ComputeServiceCategoryLabel>(() => ({
  [COMPUTE_SERVICE_CATEGORY.storage]: i18n.t('Storage'),
  [COMPUTE_SERVICE_CATEGORY.network]: i18n.t('Network'),
  [COMPUTE_SERVICE_CATEGORY.compute]: i18n.t('Compute'),
}));

export const COMPUTE_SERVICE = [
  {
    name: 'cinder-scheduler',
    title: 'Cinder scheduler',
    category: COMPUTE_SERVICE_CATEGORY.storage,
    type: COMPUTE_SERVICE_TYPE.controller
  },
  {
    name: 'cinder-volume',
    title: 'Cinder volume',
    category: COMPUTE_SERVICE_CATEGORY.storage,
    type: COMPUTE_SERVICE_TYPE.worker
  },
  {
    name: 'cinder-backup',
    title: 'Cinder backup',
    category: COMPUTE_SERVICE_CATEGORY.storage,
    type: COMPUTE_SERVICE_TYPE.worker
  },
  {
    name: 'neutron-dhcp-agent',
    title: 'Neutron DHCP agent',
    category: COMPUTE_SERVICE_CATEGORY.network,
    type: COMPUTE_SERVICE_TYPE.controller
  },
  {
    name: 'neutron-l3-agent',
    title: 'Neutron L3 agent',
    category: COMPUTE_SERVICE_CATEGORY.network,
    type: COMPUTE_SERVICE_TYPE.worker
  },
  {
    name: 'neutron-metadata-agent',
    title: 'Neutron metadata agent',
    category: COMPUTE_SERVICE_CATEGORY.network,
    type: COMPUTE_SERVICE_TYPE.worker
  },
  {
    name: 'neutron-metering-agent',
    title: 'Neutron meterging agent',
    category: COMPUTE_SERVICE_CATEGORY.network,
    type: COMPUTE_SERVICE_TYPE.worker
  },
  {
    name: 'neutron-openvswitch-agent',
    title: 'Neutron Open vSwitch agent',
    category: COMPUTE_SERVICE_CATEGORY.network,
    type: COMPUTE_SERVICE_TYPE.worker
  },
  {
    name: 'nova-compute',
    title: 'Nova compute',
    category: COMPUTE_SERVICE_CATEGORY.compute,
    type: COMPUTE_SERVICE_TYPE.worker
  },
  {
    name: 'nova-conductor',
    title: 'Nova conductor',
    category: COMPUTE_SERVICE_CATEGORY.compute,
    type: COMPUTE_SERVICE_TYPE.controller
  },
  {
    name: 'nova-scheduler',
    title: 'Nova scheduler',
    category: COMPUTE_SERVICE_CATEGORY.compute,
    type: COMPUTE_SERVICE_TYPE.controller
  }
];

export const enum COMPUTE_NODE_STATUSES {
  DISABLED = 'disabled',
  MAINTENANCE = 'maintenance',
  RELEASING = 'releasing',
  JOINING = 'joining',
  HEALTHY = 'healthy',
  CRITICAL = 'critical',
  OFFLINE = 'offline',
  WARNING = 'warning',
  RECONFIGURING = 'reconfiguring',
  RECOVERING = 'recovering',
  RECOVERY_FAILED = 'recovery failed'
}

type ComputeNodeStatusMap = Record<COMPUTE_NODE_STATUSES, StatusData>;

export const COMPUTE_NODE_STATUS_MAP = computedLocale<ComputeNodeStatusMap>(() => ({
  [COMPUTE_NODE_STATUSES.DISABLED]: {
    icon: STATUS_ICON_WARNING,
    text: i18n.t('Fenced'),
  },
  [COMPUTE_NODE_STATUSES.MAINTENANCE]: {
    icon: STATUS_ICON_MAINTENANCE,
    text: i18n.t('Maintenance'),
  },
  [COMPUTE_NODE_STATUSES.RELEASING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Releasing'),
  },
  [COMPUTE_NODE_STATUSES.JOINING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Joining'),
  },
  [COMPUTE_NODE_STATUSES.HEALTHY]: {
    icon: STATUS_ICON_CHECK_O,
    text: i18n.t('Healthy'),
  },
  [COMPUTE_NODE_STATUSES.CRITICAL]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Critical'),
  },
  [COMPUTE_NODE_STATUSES.OFFLINE]: {
    icon: STATUS_ICON_DISABLED,
    text: i18n.t('Offline'),
  },
  [COMPUTE_NODE_STATUSES.WARNING]: {
    icon: STATUS_ICON_WARNING_O,
    text: i18n.t('Warning'),
  },
  [COMPUTE_NODE_STATUSES.RECONFIGURING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Configuring'),
  },
  [COMPUTE_NODE_STATUSES.RECOVERING]: {
    icon: STATUS_ICON_LOADING,
    text: i18n.t('Recovering'),
  },
  [COMPUTE_NODE_STATUSES.RECOVERY_FAILED]: {
    icon: STATUS_ICON_TIMES_O,
    text: i18n.t('Recovery failed')
  },
}));
